import axios from "axios";
import { API_analytics_create_event } from "../constants";

// In-memory auth token. Persisted to localStorage on login so that full page
// reloads can restore the session, but also purged from localStorage when the
// app enters a shared-iPad kiosk view (Time Clock / Live Action). Keeping the
// token here lets the in-session API calls keep working after that purge,
// while a refresh wipes it and forces re-login.
let authToken = null;

export function setAuthToken(token) {
  authToken = token || null;
}

export function getAuthToken() {
  if (authToken) return authToken;
  try {
    return localStorage.getItem("token");
  } catch (error) {
    return null;
  }
}

// Remove persisted auth from localStorage while leaving the in-memory token
// intact, so the current tab's kiosk session keeps working but a refresh
// lands on the login screen. Call this only from explicit kiosk-entry user
// actions (sidebar confirmations, "Launch Clock" buttons) — never from a
// URL-driven tab change, which would silently destroy sessions for anyone
// opening a bookmark or deep link.
export function purgePersistedAuth() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("email");
    localStorage.removeItem("expirationDate");
    localStorage.removeItem("permissionRole");
  } catch (error) {
    // no-op: storage can be unavailable in some browser privacy modes
  }
}

// Sync the in-memory token when persisted auth changes in another tab.
// The `storage` event only fires in tabs that did NOT make the change, so
// the tab performing a kiosk purge still keeps its own session alive, while
// sibling tabs pick up the new state on their next request.
//   - key === null: localStorage.clear() (used by logout()) → drop token
//   - key === "token" with no newValue: removeItem("token") → drop token
//   - key === "token" with a newValue: re-auth / rotation → adopt new token
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === null) {
      authToken = null;
    } else if (event.key === "token") {
      authToken = event.newValue || null;
    }
  });
}

/*
 * Returns axios config object with authentication Token.
 */
export function getDefaultAxiosConfig() {
  return {
    headers: {
      Authorization: `Token ${getAuthToken()}`,
    },
  };
}

/**
 * RightWorkAnalytics - A class for tracking analytics events in RightWork applications
 *
 * This class provides methods to send analytics events to the RightWork backend
 * with proper authentication and formatting.
 */
class RightWorkAnalytics {
  /**
   * Send a single event to the backend
   * @param {string} eventName - Event name
   * @param {object} properties - The event data to send
   * @returns {Promise} - A promise that resolves when the event is sent
   */
  track(eventName, store_id, properties) {
    return axios
      .post(
        API_analytics_create_event,
        {
          event_name: eventName,
          store_id: store_id,
          properties: properties,
        },
        getDefaultAxiosConfig()
      )
      .catch((error) => {
        console.error("RightWorkAnalytics: Failed to send event", error);
      });
  }
}

// Create a singleton instance
export const analytics = new RightWorkAnalytics();
